import React, { useState } from "react";
import axios from "axios";
import { getApiUrl } from "../../../../../../config/api";
import { useNavigate } from "react-router-dom";

// ── Progress Steps ────────────────────────────────────────────────────────────
function ProgressSteps({ currentStep, language }) {
    const steps = [
        {
            id: 1,
            labelEn: "Upload PDF",
            labelZh: "上傳PDF",
            icon: (
                <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                    />
                </svg>
            ),
        },
        {
            id: 2,
            labelEn: "Review & Edit",
            labelZh: "審核編輯",
            icon: (
                <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                    />
                </svg>
            ),
        },
        {
            id: 3,
            labelEn: "Complete",
            labelZh: "完成",
            icon: (
                <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                    />
                </svg>
            ),
        },
    ];

    return (
        <div className="mb-8">
            <div className="flex items-center justify-between relative">
                <div
                    className="absolute top-1/3 left-5 w-11/12 h-1 bg-gray-200"
                    style={{ zIndex: 0 }}
                />
                <div
                    className="absolute top-1/3 left-5 h-1 bg-orange-500 transition-all duration-500"
                    style={{
                        width: `${((currentStep - 1) / (steps.length - 1)) * 100}%`,
                        zIndex: 0,
                    }}
                />
                {steps.map((step) => {
                    const isCompleted = currentStep > step.id;
                    const isActive = currentStep === step.id;
                    return (
                        <div
                            key={step.id}
                            className="flex flex-col items-center relative"
                            style={{ zIndex: 1 }}
                        >
                            <div
                                className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 mb-2 ${
                                    isCompleted
                                        ? "bg-orange-500 text-white shadow-lg shadow-orange-500/50"
                                        : isActive
                                          ? "bg-orange-500 text-white shadow-lg shadow-orange-500/50 scale-110"
                                          : "bg-white border-2 border-gray-300 text-gray-400"
                                }`}
                            >
                                {isCompleted ? (
                                    <svg
                                        className="w-5 h-5"
                                        fill="currentColor"
                                        viewBox="0 0 20 20"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                            clipRule="evenodd"
                                        />
                                    </svg>
                                ) : (
                                    step.icon
                                )}
                            </div>
                            <p
                                className={`text-xs font-medium text-center whitespace-nowrap transition-colors ${isActive ? "text-orange-500" : isCompleted ? "text-gray-900" : "text-gray-400"}`}
                            >
                                {language === "zh"
                                    ? step.labelZh
                                    : step.labelEn}
                            </p>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

// ── Attribute Editor row ──────────────────────────────────────────────────────
function AttributeRow({ attr, index, onChange, onRemove, language }) {
    const { attributeName, dataType, data } = attr;

    const dataStr =
        dataType === "string" ? data : JSON.stringify(data, null, 2);

    const handleDataChange = (raw) => {
        if (dataType === "string") {
            onChange(index, { ...attr, data: raw });
        } else {
            try {
                onChange(index, { ...attr, data: JSON.parse(raw) });
            } catch {
                // keep raw string while user is typing
                onChange(index, { ...attr, data: raw });
            }
        }
    };

    return (
        <div className="bg-gray-50 rounded-xl border border-gray-200 overflow-hidden">
            <div className="flex items-center gap-3 px-4 py-3 bg-orange-500/5 border-b border-orange-500/20">
                <div className="w-2 h-2 rounded-full bg-orange-500 flex-shrink-0" />
                <input
                    value={attributeName}
                    onChange={(e) =>
                        onChange(index, {
                            ...attr,
                            attributeName: e.target.value,
                        })
                    }
                    className="flex-1 text-sm font-semibold text-gray-700 bg-transparent border-none outline-none"
                    placeholder={
                        language === "zh" ? "屬性名稱" : "Attribute name"
                    }
                />
                <select
                    value={dataType}
                    onChange={(e) =>
                        onChange(index, {
                            ...attr,
                            dataType: e.target.value,
                            data: e.target.value === "string" ? "" : [],
                        })
                    }
                    className="text-xs border border-gray-200 rounded-lg px-2 py-1 bg-white text-gray-600 focus:outline-none focus:ring-2 focus:ring-orange-400"
                >
                    <option value="string">string</option>
                    <option value="list">list</option>
                    <option value="table">table</option>
                </select>
                <button
                    onClick={() => onRemove(index)}
                    className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                >
                    <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                        />
                    </svg>
                </button>
            </div>
            <div className="p-3">
                <textarea
                    value={dataStr}
                    onChange={(e) => handleDataChange(e.target.value)}
                    rows={dataType === "string" ? 2 : 5}
                    className="w-full text-sm text-gray-700 bg-white border border-gray-200 rounded-lg px-3 py-2 font-mono resize-y focus:outline-none focus:ring-2 focus:ring-orange-400"
                    placeholder={
                        dataType === "string"
                            ? "Value…"
                            : 'JSON array, e.g. ["item1","item2"]'
                    }
                />
                {dataType !== "string" && (
                    <p className="text-xs text-gray-400 mt-1">
                        {language === "zh"
                            ? "請輸入有效的 JSON 陣列"
                            : "Enter valid JSON array (list) or array-of-objects (table)"}
                    </p>
                )}
            </div>
        </div>
    );
}

// ── Main Component ────────────────────────────────────────────────────────────
function UploadPdf({
    language,
    currentUser,
    file,
    setFile,
    status,
    setStatus,
    loading,
    setLoading,
    selection,
    onBack,
    onDone,
}) {
    const [step, setStep] = useState(1);
    // Only the reset is used today; the extracted payload is not rendered yet.
    const [, setExtracted] = useState(null);

    // Editable fields (populated after extraction)
    const [modelId, setModelId] = useState("");
    const [description, setDescription] = useState("");
    const [pdfUrl, setPdfUrl] = useState("");
    const [rohsCompliant, setRohsCompliant] = useState(false);
    const [reachCompliant, setReachCompliant] = useState(false);
    const [ulComparable, setUlComparable] = useState(false);
    const [attributes, setAttributes] = useState([]);
    const [saving, setSaving] = useState(false);

    const navigate = useNavigate();

    // ── Step 1 handlers ───────────────────────────────────────────────────
    const handleFileChange = (e) => {
        setFile(e.target.files[0]);
        setStatus("");
    };
    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };
    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const f = e.dataTransfer.files[0];
        if (f && f.type === "application/pdf") {
            setFile(f);
            setStatus("");
        }
    };

    const handleUpload = async (e) => {
        e.preventDefault();
        if (!file || !currentUser) return;
        setLoading(true);
        const formData = new FormData();
        formData.append("pdf", file);
        formData.append("competitor_name", selection.producerName);
        console.log("selection in upload handler:", selection);
        try {
            const token = await currentUser.getIdToken();
            const res = await axios.post(
                `${getApiUrl()}/pdf/parse-competitor`,
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                        Authorization: `Bearer ${token}`,
                    },
                },
            );
            const data = res.data;

            console.log("Extracted data:", data);
            // setExtracted(data);
            // setModelId(data.model_id || "");
            // setDescription(data.description || "");
            // setPdfUrl(data.pdf_url || "");
            // setRohsCompliant(data.compliance?.rohs_compliant ?? false);
            // setReachCompliant(data.compliance?.reach_compliant ?? false);
            // setUlComparable(data.compliance?.ul_comparable ?? false);
            // setAttributes(data.attributes || []);
            // setStep(2);
            navigate(
                {
                    pathname: "/competitor-product-properties",
                    search: `?lang=${language}`,
                },
                {
                    state: {
                        data: data,
                        selection: selection,
                        file: file,
                    },
                },
            );
        } catch (err) {
            const msg =
                err.response?.data?.error || err.message || "Unknown error";
            setStatus(
                language === "zh" ? `上傳錯誤: ${msg}` : `Upload error: ${msg}`,
            );
        } finally {
            setLoading(false);
        }
    };

    // ── Step 2 handlers ───────────────────────────────────────────────────
    const handleAttrChange = (i, updated) =>
        setAttributes((prev) =>
            prev.map((a, idx) => (idx === i ? updated : a)),
        );
    const handleAttrRemove = (i) =>
        setAttributes((prev) => prev.filter((_, idx) => idx !== i));
    const handleAttrAdd = () =>
        setAttributes((prev) => [
            ...prev,
            { attributeName: "", dataType: "string", data: "" },
        ]);

    const handleSave = async () => {
        if (!currentUser) return;
        setSaving(true);
        try {
            const token = await currentUser.getIdToken();
            await axios.post(
                `${getApiUrl()}/api/v2/competitor-products/`,
                {
                    model_id: modelId,
                    category_id: selection.categoryId,
                    producer_id: selection.producerId,
                    description,
                    pdf_url: pdfUrl || null,
                    rohs_compliant: rohsCompliant,
                    reach_compliant: reachCompliant,
                    ul_comparable: ulComparable,
                    attributes,
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                },
            );
            setStep(3);
        } catch (err) {
            const msg = err.response?.data?.error || err.message;
            setStatus(
                language === "zh" ? `儲存失敗: ${msg}` : `Save failed: ${msg}`,
            );
        } finally {
            setSaving(false);
        }
    };

    // ── Step 3 — Success ──────────────────────────────────────────────────
    if (step === 3) {
        return (
            <div className="flex justify-center">
                <div className="w-full max-w-2xl">
                    <ProgressSteps currentStep={3} language={language} />
                    <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center">
                        <div className="w-20 h-20 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
                            <svg
                                className="w-10 h-10 text-green-500"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M5 13l4 4L19 7"
                                />
                            </svg>
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">
                            {language === "zh"
                                ? "上傳成功！"
                                : "Upload Successful!"}
                        </h2>
                        <p className="text-gray-500 mb-8">
                            {language === "zh"
                                ? `競品「${modelId}」已成功建立。`
                                : `Competitor product "${modelId}" has been created successfully.`}
                        </p>
                        <div className="flex gap-3 justify-center">
                            <button
                                onClick={onDone}
                                className="px-6 py-2.5 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-xl transition-colors shadow-md shadow-orange-500/20"
                            >
                                {language === "zh" ? "完成" : "Done"}
                            </button>
                            <button
                                onClick={() => {
                                    setStep(1);
                                    setFile(null);
                                    setStatus("");
                                    setExtracted(null);
                                }}
                                className="px-6 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-xl transition-colors"
                            >
                                {language === "zh"
                                    ? "再次上傳"
                                    : "Upload Another"}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // ── Step 2 — Review & Edit ────────────────────────────────────────────
    if (step === 2) {
        return (
            <div className="flex justify-center">
                <div className="w-full max-w-2xl">
                    <button
                        onClick={() => setStep(1)}
                        className="mb-6 inline-flex items-center gap-2 text-gray-600 hover:text-orange-500 transition-colors font-medium"
                    >
                        <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M15 19l-7-7 7-7"
                            />
                        </svg>
                        {language === "zh" ? "返回上傳" : "Back to Upload"}
                    </button>
                    <ProgressSteps currentStep={2} language={language} />

                    <div className="bg-white rounded-2xl border border-gray-200 p-8 space-y-6">
                        {/* Context banner */}
                        <div className="px-4 py-3 bg-orange-50 border border-orange-200 rounded-xl text-sm text-orange-700 flex items-center gap-2">
                            <svg
                                className="w-4 h-4 flex-shrink-0"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                            </svg>
                            <span>
                                <span className="font-semibold">
                                    {selection.producerName}
                                </span>
                                {" › "}
                                <span className="font-semibold">
                                    {selection.categoryName}
                                </span>
                            </span>
                        </div>

                        {/* Model ID */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                {language === "zh" ? "型號" : "Model ID"}
                                <span className="text-red-400 ml-0.5">*</span>
                            </label>
                            <input
                                value={modelId}
                                onChange={(e) => setModelId(e.target.value)}
                                className="w-full px-4 py-2.5 text-sm border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:ring-4 focus:ring-orange-500/10 outline-none transition-all"
                                placeholder="e.g. TFLEX-SF800"
                            />
                        </div>

                        {/* Description */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                {language === "zh" ? "描述" : "Description"}
                            </label>
                            <textarea
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                rows={3}
                                className="w-full px-4 py-2.5 text-sm border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:ring-4 focus:ring-orange-500/10 outline-none transition-all resize-y"
                            />
                        </div>

                        {/* PDF URL */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                {language === "zh"
                                    ? "PDF 連結 (選填)"
                                    : "PDF URL (optional)"}
                            </label>
                            <input
                                value={pdfUrl}
                                onChange={(e) => setPdfUrl(e.target.value)}
                                className="w-full px-4 py-2.5 text-sm border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:ring-4 focus:ring-orange-500/10 outline-none transition-all font-mono"
                                placeholder="https://..."
                            />
                        </div>

                        {/* Compliance checkboxes */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-3">
                                {language === "zh"
                                    ? "法規符合性"
                                    : "Compliance"}
                            </label>
                            <div className="flex flex-wrap gap-4">
                                {[
                                    {
                                        key: "rohs",
                                        label: "RoHS",
                                        val: rohsCompliant,
                                        set: setRohsCompliant,
                                    },
                                    {
                                        key: "reach",
                                        label: "REACH",
                                        val: reachCompliant,
                                        set: setReachCompliant,
                                    },
                                    {
                                        key: "ul",
                                        label: "UL Comparable",
                                        val: ulComparable,
                                        set: setUlComparable,
                                    },
                                ].map(({ key, label, val, set }) => (
                                    <label
                                        key={key}
                                        className="inline-flex items-center gap-2 cursor-pointer select-none"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={val}
                                            onChange={(e) =>
                                                set(e.target.checked)
                                            }
                                            className="w-4 h-4 rounded border-gray-300 text-orange-500 focus:ring-orange-400"
                                        />
                                        <span className="text-sm text-gray-700">
                                            {label}
                                        </span>
                                    </label>
                                ))}
                            </div>
                        </div>

                        {/* Attributes */}
                        <div>
                            <div className="flex items-center justify-between mb-3">
                                <label className="block text-sm font-semibold text-gray-700">
                                    {language === "zh" ? "屬性" : "Attributes"}
                                </label>
                                <button
                                    onClick={handleAttrAdd}
                                    className="inline-flex items-center gap-1.5 text-xs font-medium text-orange-600 bg-orange-500/10 hover:bg-orange-500/20 px-3 py-1.5 rounded-lg transition-colors"
                                >
                                    <svg
                                        className="w-3.5 h-3.5"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M12 4v16m8-8H4"
                                        />
                                    </svg>
                                    {language === "zh"
                                        ? "新增屬性"
                                        : "Add Attribute"}
                                </button>
                            </div>
                            <div className="space-y-3">
                                {attributes.map((attr, i) => (
                                    <AttributeRow
                                        key={i}
                                        attr={attr}
                                        index={i}
                                        onChange={handleAttrChange}
                                        onRemove={handleAttrRemove}
                                        language={language}
                                    />
                                ))}
                                {attributes.length === 0 && (
                                    <p className="text-sm text-gray-400 text-center py-4">
                                        {language === "zh"
                                            ? "尚無屬性"
                                            : "No attributes yet"}
                                    </p>
                                )}
                            </div>
                        </div>

                        {/* Status */}
                        {status && (
                            <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-red-600 text-sm">
                                {status}
                            </div>
                        )}

                        {/* Actions */}
                        <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                            <button
                                onClick={() => setStep(1)}
                                className="px-5 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
                            >
                                {language === "zh" ? "返回" : "Back"}
                            </button>
                            <button
                                onClick={handleSave}
                                disabled={saving || !modelId.trim()}
                                className={`px-6 py-2.5 text-sm font-semibold rounded-xl transition-all duration-200 flex items-center gap-2 ${
                                    saving || !modelId.trim()
                                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                        : "bg-orange-500 text-white hover:bg-orange-600 shadow-md shadow-orange-500/20"
                                }`}
                            >
                                {saving ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                        {language === "zh"
                                            ? "儲存中…"
                                            : "Saving…"}
                                    </>
                                ) : (
                                    <>
                                        {language === "zh"
                                            ? "儲存競品"
                                            : "Save Product"}
                                        <svg
                                            className="w-4 h-4"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M5 13l4 4L19 7"
                                            />
                                        </svg>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // ── Step 1 — Upload PDF ───────────────────────────────────────────────
    return (
        <div className="flex justify-center">
            <div className="w-full max-w-2xl">
                <button
                    onClick={onBack}
                    className="mb-6 inline-flex items-center gap-2 text-gray-600 hover:text-orange-500 transition-colors font-medium"
                >
                    <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 19l-7-7 7-7"
                        />
                    </svg>
                    {language === "zh" ? "返回選擇" : "Back to Selection"}
                </button>
                <ProgressSteps currentStep={1} language={language} />

                <div className="bg-white rounded-2xl border border-gray-200 p-8 relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-40 h-40 bg-orange-500/5 rounded-full -translate-y-1/2 translate-x-1/2" />
                    <div className="absolute bottom-0 left-0 w-32 h-32 bg-orange-500/5 rounded-full translate-y-1/2 -translate-x-1/2" />

                    {/* Header */}
                    <div className="relative mb-8 text-center">
                        <div className="inline-flex items-center justify-center w-16 h-16 bg-orange-500/10 rounded-2xl mb-4">
                            <svg
                                className="w-8 h-8 text-orange-500"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                                />
                            </svg>
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-1">
                            {language === "zh"
                                ? "上傳競品 PDF"
                                : "Upload Competitor PDF"}
                        </h2>
                        <div className="text-sm text-orange-600 font-medium">
                            {selection.producerName} › {selection.categoryName}
                        </div>
                    </div>

                    {/* Loading overlay */}
                    {loading && (
                        <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/95 backdrop-blur-sm z-10 rounded-2xl">
                            <div className="relative mb-4">
                                <div className="w-16 h-16 border-4 border-orange-500/30 rounded-full" />
                                <div className="absolute top-0 left-0 w-16 h-16 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
                            </div>
                            <p className="text-gray-900 font-medium">
                                {language === "zh"
                                    ? "處理中..."
                                    : "Processing..."}
                            </p>
                            <p className="text-gray-600 text-sm mt-1">
                                {language === "zh"
                                    ? "正在分析您的PDF文件"
                                    : "Analyzing your PDF file"}
                            </p>
                        </div>
                    )}

                    <div className="relative space-y-6">
                        {/* Drop zone */}
                        <div
                            className={`relative border-2 border-dashed rounded-2xl p-10 text-center transition-all duration-300 cursor-pointer ${
                                file
                                    ? "border-orange-500/50 bg-orange-500/5"
                                    : "border-gray-300 hover:border-orange-500/50 hover:bg-gray-50"
                            }`}
                            onDragOver={handleDragOver}
                            onDrop={handleDrop}
                            onClick={() =>
                                document.getElementById("cp-pdf-upload").click()
                            }
                        >
                            <div
                                className={`mb-4 inline-flex items-center justify-center w-16 h-16 rounded-2xl transition-all duration-300 ${file ? "bg-orange-500/20" : "bg-gray-100"}`}
                            >
                                {file ? (
                                    <svg
                                        className="w-8 h-8 text-orange-500"
                                        fill="currentColor"
                                        viewBox="0 0 20 20"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                            clipRule="evenodd"
                                        />
                                    </svg>
                                ) : (
                                    <svg
                                        className="w-8 h-8 text-gray-400"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                                        />
                                    </svg>
                                )}
                            </div>
                            <div className="text-gray-700 mb-4">
                                {file ? (
                                    <span className="text-orange-500 font-medium">
                                        {language === "zh"
                                            ? "文件已選擇"
                                            : "File selected"}
                                    </span>
                                ) : (
                                    <>
                                        <span className="text-orange-500 font-medium">
                                            {language === "zh"
                                                ? "點擊上傳"
                                                : "Click to upload"}
                                        </span>{" "}
                                        {language === "zh"
                                            ? "或拖放文件到此處"
                                            : "or drag and drop"}
                                    </>
                                )}
                            </div>
                            <p className="text-gray-500 text-sm">
                                {language === "zh"
                                    ? "僅支持 PDF 格式"
                                    : "PDF files only"}
                            </p>
                            <input
                                id="cp-pdf-upload"
                                type="file"
                                accept="application/pdf"
                                onChange={handleFileChange}
                                className="hidden"
                            />
                        </div>

                        {/* File info */}
                        {file && (
                            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 animate-fadeIn">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-4">
                                        <div className="w-12 h-12 bg-red-500/10 rounded-xl flex items-center justify-center">
                                            <svg
                                                className="w-6 h-6 text-red-500"
                                                fill="currentColor"
                                                viewBox="0 0 20 20"
                                            >
                                                <path
                                                    fillRule="evenodd"
                                                    d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z"
                                                    clipRule="evenodd"
                                                />
                                            </svg>
                                        </div>
                                        <div>
                                            <p className="text-gray-900 font-medium truncate max-w-[200px] sm:max-w-[300px]">
                                                {file.name}
                                            </p>
                                            <p className="text-gray-600 text-sm">
                                                {(
                                                    file.size /
                                                    1024 /
                                                    1024
                                                ).toFixed(2)}{" "}
                                                MB
                                            </p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setFile(null);
                                        }}
                                        className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                                    >
                                        <svg
                                            className="w-5 h-5"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M6 18L18 6M6 6l12 12"
                                            />
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Upload button */}
                        <button
                            onClick={handleUpload}
                            disabled={!file || loading}
                            className={`w-full py-4 px-6 rounded-xl font-medium text-lg transition-all duration-300 flex items-center justify-center gap-3 ${
                                file
                                    ? "bg-gradient-to-r from-orange-500 to-orange-600 text-white hover:from-orange-600 hover:to-orange-700 shadow-lg shadow-orange-500/30 hover:-translate-y-0.5"
                                    : "bg-gray-200 text-gray-400 cursor-not-allowed"
                            }`}
                        >
                            {loading ? (
                                <>
                                    <svg
                                        className="w-5 h-5 animate-spin"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                    >
                                        <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                        />
                                        <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                        />
                                    </svg>
                                    {language === "zh"
                                        ? "處理中..."
                                        : "Processing..."}
                                </>
                            ) : (
                                <>
                                    <svg
                                        className="w-5 h-5"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                                        />
                                    </svg>
                                    {language === "zh"
                                        ? "上傳並分析"
                                        : "Upload & Analyze"}
                                </>
                            )}
                        </button>

                        {/* Status */}
                        {status && (
                            <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-red-600 text-sm text-center">
                                {status}
                            </div>
                        )}
                    </div>
                </div>

                {/* Info cards */}
                <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                    {[
                        {
                            color: "blue",
                            icon: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z",
                            titleEn: "Supported Format",
                            titleZh: "支持的格式",
                            descEn: "PDF",
                            descZh: "PDF",
                        },
                        {
                            color: "green",
                            icon: "M13 10V3L4 14h7v7l9-11h-7z",
                            titleEn: "Auto-Extract",
                            titleZh: "自動提取",
                            descEn: "AI Powered",
                            descZh: "AI 驅動",
                        },
                        {
                            color: "purple",
                            icon: "M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z",
                            titleEn: "Editable",
                            titleZh: "可編輯",
                            descEn: "Review after upload",
                            descZh: "上傳後審核",
                        },
                    ].map(
                        ({ color, icon, titleEn, titleZh, descEn, descZh }) => (
                            <div
                                key={color}
                                className="bg-white border border-gray-200 rounded-xl p-4 text-center"
                            >
                                <div
                                    className={`w-10 h-10 bg-${color}-500/10 rounded-lg flex items-center justify-center mx-auto mb-3`}
                                >
                                    <svg
                                        className={`w-5 h-5 text-${color}-500`}
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d={icon}
                                        />
                                    </svg>
                                </div>
                                <h4 className="text-gray-900 font-medium text-sm mb-1">
                                    {language === "zh" ? titleZh : titleEn}
                                </h4>
                                <p className="text-gray-600 text-xs">
                                    {language === "zh" ? descZh : descEn}
                                </p>
                            </div>
                        ),
                    )}
                </div>
            </div>
        </div>
    );
}

export default UploadPdf;
